import { useState, useCallback, useRef } from 'react';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useToast } from '../../../../hooks/useToast';
import { useInvalidateQueries } from '../../../../hooks/useApi';
import { knowledgeService } from '../../../../services/knowledgeService';
import type { KnowledgeDocument } from '../../../../services/knowledgeService';

export interface UploadFileItem {
  /** Client-side unique ID */
  id: string;
  /** The raw File object */
  file: File;
  /** User-editable title (auto-filled from filename) */
  title: string;
  /** Optional description */
  description: string;
  /** Tags for the document */
  tags: string[];
  /** Upload progress 0–100 */
  progress: number;
  /** Upload status */
  status: 'pending' | 'uploading' | 'success' | 'error';
  /** Error message if failed */
  errorMessage?: string;
  /** The created document (on success) */
  document?: KnowledgeDocument;
}

interface UseUploadDocumentsReturn {
  files: UploadFileItem[];
  addFiles: (newFiles: File[]) => string[];
  removeFile: (id: string) => void;
  updateFile: (id: string, updates: Partial<Pick<UploadFileItem, 'title' | 'description' | 'tags'>>) => void;
  uploadAll: () => Promise<void>;
  clearCompleted: () => void;
  reset: () => void;
  isUploading: boolean;
  hasErrors: boolean;
  allComplete: boolean;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_BATCH_SIZE = 10;

const ACCEPTED_MIME_TYPES: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/markdown': 'md',
  'text/html': 'html',
  'application/json': 'json',
};

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.pptx', '.csv', '.txt', '.xlsx', '.md', '.html', '.json'];

function generateId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getFileTitleFromName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return `File exceeds maximum size of 50MB (${(file.size / (1024 * 1024)).toFixed(1)}MB)`;
  }

  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  const mimeOk = Object.keys(ACCEPTED_MIME_TYPES).includes(file.type);
  const extOk = ACCEPTED_EXTENSIONS.includes(ext);

  if (!mimeOk && !extOk) {
    return `File type "${file.type || ext}" is not supported. Supported: PDF, DOCX, PPTX, CSV, TXT, XLSX, MD, HTML, JSON`;
  }

  return null;
}

export function useUploadDocuments(): UseUploadDocumentsReturn {
  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const isUploadingRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const toast = useToast();
  const invalidate = useInvalidateQueries();

  const addFiles = useCallback((newFiles: File[]): string[] => {
    const addedIds: string[] = [];

    setFiles((prev) => {
      const remaining = MAX_BATCH_SIZE - prev.length;
      if (remaining <= 0) {
        toast.warning('Batch limit', `Maximum ${MAX_BATCH_SIZE} files per batch.`);
        return prev;
      }

      const filesToAdd = newFiles.slice(0, remaining);
      if (newFiles.length > remaining) {
        toast.warning('Batch limit', `Only ${remaining} more file(s) can be added. ${newFiles.length - remaining} file(s) skipped.`);
      }

      const newItems: UploadFileItem[] = filesToAdd.map((file) => {
        const validationError = validateFile(file);
        const id = generateId();
        addedIds.push(id);

        return {
          id,
          file,
          title: getFileTitleFromName(file.name),
          description: '',
          tags: [],
          progress: 0,
          status: validationError ? 'error' : 'pending',
          errorMessage: validationError || undefined,
        };
      });

      return [...prev, ...newItems];
    });

    return addedIds;
  }, [toast]);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const updateFile = useCallback((id: string, updates: Partial<Pick<UploadFileItem, 'title' | 'description' | 'tags'>>) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
    );
  }, []);

  const uploadAll = useCallback(async () => {
    if (!workspaceId) {
      toast.error('Error', 'No workspace selected.');
      return;
    }

    if (isUploadingRef.current) return;
    isUploadingRef.current = true;
    setIsUploading(true);

    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.info('Nothing to upload', 'No pending files to upload.');
      isUploadingRef.current = false;
      setIsUploading(false);
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const fileItem of pendingFiles) {
      // Mark as uploading
      setFiles((prev) =>
        prev.map((f) => (f.id === fileItem.id ? { ...f, status: 'uploading' as const, progress: 0 } : f))
      );

      try {
        const document = await knowledgeService.uploadWithProgress(
          workspaceId,
          {
            file: fileItem.file,
            title: fileItem.title.trim() || getFileTitleFromName(fileItem.file.name),
            description: fileItem.description.trim() || undefined,
            tags: fileItem.tags.length > 0 ? fileItem.tags : undefined,
          },
          (progress) => {
            setFiles((prev) =>
              prev.map((f) => (f.id === fileItem.id ? { ...f, progress } : f))
            );
          }
        );

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id
              ? { ...f, status: 'success' as const, progress: 100, document }
              : f
          )
        );
        successCount++;
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || 'Upload failed';
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id
              ? { ...f, status: 'error' as const, progress: 0, errorMessage: message }
              : f
          )
        );
        errorCount++;
      }
    }

    // Invalidate knowledge documents query
    await invalidate(['knowledge-documents']);

    if (successCount > 0 && errorCount === 0) {
      toast.success('Upload complete', `${successCount} document(s) uploaded successfully.`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning('Partial upload', `${successCount} uploaded, ${errorCount} failed.`);
    } else if (errorCount > 0) {
      toast.error('Upload failed', `All ${errorCount} file(s) failed to upload.`);
    }

    isUploadingRef.current = false;
    setIsUploading(false);
  }, [files, workspaceId, toast, invalidate]);

  const clearCompleted = useCallback(() => {
    setFiles((prev) => prev.filter((f) => f.status !== 'success'));
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    isUploadingRef.current = false;
    setIsUploading(false);
  }, []);

  const hasErrors = files.some((f) => f.status === 'error');
  const allComplete =
    files.length > 0 && files.every((f) => f.status === 'success' || f.status === 'error');

  return {
    files,
    addFiles,
    removeFile,
    updateFile,
    uploadAll,
    clearCompleted,
    reset,
    isUploading,
    hasErrors,
    allComplete,
  };
}